import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./Workshops.css";
import LoadingOverlay from "../../components/Loading/LoadingOverlay";
import CustomAlert from "../../components/Alerts/CustomAlert";
import Modal from "../../components/Dialog/Modal";
import { addAlert, removeAlert } from "../../Utils/helper";
import MultiPicker from "./MultiPicker";
import {
  MEDIA_CATEGORY_OPTIONS,
  VIDEO_TYPEOF_OPTIONS,
  WORKSHOP_TYPE_OPTIONS,
  createWorkshop,
  getWorkshop,
  listAudiences,
  listCategories,
  listExperts,
  listTags,
  listWhatYouLearn,
  listWhyWorkshop,
  listWorkshopTypes,
  updateWorkshop,
  uploadWorkshopBanner,
} from "../../Utils/workshopApi";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STEPS = [
  { key: "basics", label: "Basics", sub: "Title, type, banner" },
  { key: "pricing", label: "Pricing & seats", sub: "MRP, selling price" },
  { key: "curriculum", label: "Curriculum", sub: "Sections & videos" },
  { key: "extras", label: "Highlights", sub: "Why & what you learn" },
  { key: "review", label: "Review & publish", sub: "Final check" },
];

const blankWorkshop = () => ({
  title: "",
  description: "",
  about_workshop: "",
  workshop_type: "",
  type: null,
  category: null,
  categories: [],
  intended_audience: null,
  audience: [],
  tags: [],
  workshop_experts: [],
  why_this_works: [],
  what_you_learn: [],
  mrp: "",
  selling_price: "",
  discount: "",
  mrp_usd: "",
  selling_price_usd: "",
  discount_for_usd: "",
  seats: "",
  is_unlimited_seats: false,
  is_active: false,
  is_first_chapter_free: false,
  is_live_recorded_hybrid: false,
  free_trial: false,
  show_workshopcovers: true,
  live_workshop_url: "",
  whatsapp_group_url: "",
  recorded_session_url: "",
  start_date_time: "",
  end_date_time: "",
  is_date_only: false,
  index: 0,
  sections: [],
  faqs: [],
  media: [],
});

// Convert ID-or-{id,title} into plain id
const toId = (x) => (x && typeof x === "object" ? x.id : x ?? null);
const toIds = (arr) => (Array.isArray(arr) ? arr.map(toId).filter(Boolean) : []);

// Convert ISO datetime from API to value usable by <input type="datetime-local">
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const toIsoFromLocal = (local) => {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const reindex = (arr) => arr.map((item, i) => ({ ...item, index: i }));

// ---------------------------------------------------------------------------

const WorkshopForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [step, setStep] = useState(0);
  const [data, setData] = useState(blankWorkshop());
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Reference data
  const [refs, setRefs] = useState({
    workshopTypes: [],
    categories: [],
    audiences: [],
    tags: [],
    experts: [],
    whyWorkshop: [],
    whatYouLearn: [],
  });

  const dirtyRef = useRef(false);
  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  const handleAlert = (title, message, type) =>
    addAlert(title, message, type, true, setAlerts);
  const handleRemoveAlert = (alertId) => removeAlert(alertId, setAlerts);

  // ----- Initial load: ref data + (if edit) workshop data ------------------

  useEffect(() => {
    let cancelled = false;
    const loadRefs = async () => {
      try {
        const [
          workshopTypes,
          categories,
          audiences,
          tags,
          experts,
          whyWorkshop,
          whatYouLearn,
        ] = await Promise.all([
          listWorkshopTypes().catch(() => []),
          listCategories().catch(() => []),
          listAudiences().catch(() => []),
          listTags().catch(() => []),
          listExperts().catch(() => []),
          listWhyWorkshop().catch(() => []),
          listWhatYouLearn().catch(() => []),
        ]);
        if (!cancelled) {
          setRefs({
            workshopTypes,
            categories,
            audiences,
            tags,
            experts,
            whyWorkshop,
            whatYouLearn,
          });
        }
      } catch (err) {
        console.error("Failed to load reference data", err);
      }
    };
    loadRefs();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    const loadWorkshop = async () => {
      setIsLoading(true);
      try {
        const ws = await getWorkshop(id);
        if (cancelled) return;
        // Map API → form shape
        setData({
          ...blankWorkshop(),
          ...ws,
          // normalise relations to ids
          type: toId(ws.type),
          category: toId(ws.category),
          intended_audience: toId(ws.intended_audience),
          categories: toIds(ws.categories),
          audience: toIds(ws.audience),
          tags: toIds(ws.tags),
          workshop_experts: toIds(ws.workshop_experts),
          why_this_works: toIds(ws.why_this_works),
          what_you_learn: toIds(ws.what_you_learn),
          // datetimes
          start_date_time: isoToLocalInput(ws.start_date_time),
          end_date_time: isoToLocalInput(ws.end_date_time),
          // Nested relations come back via reverse-relation keys; serializer
          // exposes them under sections/faqs/media on read.
          sections: (ws.sections || []).map((s) => ({
            ...s,
            chapters: (s.chapters || []).map((c) => ({
              ...c,
              videos: (c.videos || []).map((v) => ({ ...v })),
            })),
          })),
          faqs: (ws.faqs || []).map((f) => ({
            ...f,
            answers: (f.answers || []).map((a) => ({ ...a })),
          })),
          media: (ws.media || []).map((m) => ({ ...m })),
        });
        if (ws.file) setBannerPreview(ws.file);
      } catch (err) {
        console.error("Failed to load workshop", err);
        handleAlert("Error", "Could not load this workshop.", "error");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadWorkshop();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ----- Field setters -----------------------------------------------------

  const set = (field) => (val) => {
    setData((d) => ({ ...d, [field]: val }));
    setDirty(true);
  };
  const setEv = (field) => (e) => set(field)(e.target.value);
  const setNum = (field) => (e) => {
    const v = e.target.value;
    setData((d) => ({ ...d, [field]: v === "" ? "" : Number(v) }));
    setDirty(true);
  };
  const setBool = (field) => (e) => set(field)(e.target.checked);

  // ----- Validation per step ----------------------------------------------

  const validateStep = (idx) => {
    const e = {};
    if (idx === 0) {
      if (!data.title?.trim()) e.title = "Title is required";
      if (!data.workshop_type) e.workshop_type = "Select a category";
      if (!data.about_workshop?.trim())
        e.about_workshop = "Tell us about the workshop";
      if (!data.description?.trim())
        e.description = "Add a short description";
    }
    if (idx === 1) {
      if (
        data.mrp !== "" &&
        data.selling_price !== "" &&
        Number(data.selling_price) > Number(data.mrp)
      ) {
        e.selling_price = "Selling price cannot exceed MRP";
      }
      if (!data.is_unlimited_seats && data.seats !== "" && data.seats < 0) {
        e.seats = "Seats must be 0 or more";
      }
    }
    if (idx === 2) {
      // Curriculum is optional but if a section exists, it must have a title
      data.sections.forEach((s, si) => {
        if (!s.title?.trim()) e[`section_${si}_title`] = "Section title required";
      });
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => {
    if (!validateStep(step)) {
      handleAlert("Fix the errors", "Please review highlighted fields.", "error");
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const goPrev = () => setStep((s) => Math.max(0, s - 1));
  const jumpTo = (i) => {
    if (i <= step) return setStep(i);
    if (validateStep(step)) setStep(i);
  };

  // ----- Submit ------------------------------------------------------------

  const buildPayload = (isPublish = null) => {
    const payload = {
      title: data.title?.trim(),
      description: data.description?.trim() || "",
      about_workshop: data.about_workshop?.trim() || "",
      workshop_type: data.workshop_type || null,
      type: data.type || null,
      category: data.category || null,
      categories: data.categories,
      intended_audience: data.intended_audience || null,
      audience: data.audience,
      tags: data.tags,
      workshop_experts: data.workshop_experts,
      why_this_works: data.why_this_works,
      what_you_learn: data.what_you_learn,
      mrp: data.mrp === "" ? null : Number(data.mrp),
      selling_price:
        data.selling_price === "" ? null : Number(data.selling_price),
      discount: data.discount === "" ? null : Number(data.discount),
      mrp_usd: data.mrp_usd === "" ? null : Number(data.mrp_usd),
      selling_price_usd:
        data.selling_price_usd === "" ? null : Number(data.selling_price_usd),
      discount_for_usd:
        data.discount_for_usd === "" ? null : Number(data.discount_for_usd),
      seats: data.is_unlimited_seats
        ? null
        : data.seats === ""
        ? null
        : Number(data.seats),
      is_unlimited_seats: !!data.is_unlimited_seats,
      is_first_chapter_free: !!data.is_first_chapter_free,
      is_live_recorded_hybrid: !!data.is_live_recorded_hybrid,
      free_trial: !!data.free_trial,
      show_workshopcovers: !!data.show_workshopcovers,
      live_workshop_url: data.live_workshop_url || "",
      whatsapp_group_url: data.whatsapp_group_url || "",
      recorded_session_url: data.recorded_session_url || "",
      start_date_time: toIsoFromLocal(data.start_date_time),
      end_date_time: toIsoFromLocal(data.end_date_time),
      is_date_only: !!data.is_date_only,
      index: Number(data.index || 0),
      sections: reindex(data.sections).map((s) => ({
        ...(s.id ? { id: s.id } : {}),
        title: s.title || "",
        description: s.description || "",
        index: s.index,
        chapters: reindex(s.chapters || []).map((c) => ({
          ...(c.id ? { id: c.id } : {}),
          title: c.title || "",
          description: c.description || "",
          index: c.index,
          videos: reindex(c.videos || []).map((v) => ({
            ...(v.id ? { id: v.id } : {}),
            title: v.title || "",
            description: v.description || "",
            index: v.index,
            video_url: v.video_url || "",
            typeof: v.typeof || null,
            duration: v.duration || null,
            is_featured: !!v.is_featured,
            is_published: v.is_published !== false,
          })),
        })),
      })),
      faqs: reindex(data.faqs).map((f) => ({
        ...(f.id ? { id: f.id } : {}),
        title: f.title || "",
        index: f.index,
        answers: (f.answers || []).map((a) => ({
          ...(a.id ? { id: a.id } : {}),
          answer: a.answer || "",
        })),
      })),
      media: reindex(data.media).map((m) => ({
        ...(m.id ? { id: m.id } : {}),
        title: m.title || "",
        index: m.index,
        media_url: m.media_url || "",
        category: m.category || 2,
        video_length: m.video_length || null,
      })),
    };
    if (isPublish !== null) payload.is_active = isPublish;
    else payload.is_active = !!data.is_active;
    return payload;
  };

  const submit = async (publish) => {
    // run all validations
    let allOk = true;
    for (let i = 0; i < STEPS.length; i++) {
      if (!validateStep(i)) {
        allOk = false;
        setStep(i);
        break;
      }
    }
    if (!allOk) {
      handleAlert("Fix the errors", "Please review highlighted fields.", "error");
      return;
    }
    setIsSaving(true);
    try {
      const payload = buildPayload(publish);
      let saved;
      if (isEdit) {
        saved = await updateWorkshop(id, payload);
      } else {
        saved = await createWorkshop(payload);
      }
      const savedId = saved?.id || id;
      // Upload banner if a new file is selected
      if (bannerFile && savedId) {
        try {
          await uploadWorkshopBanner(savedId, bannerFile);
        } catch (err) {
          console.error("Banner upload failed", err);
          handleAlert(
            "Saved with warning",
            "Workshop saved but banner upload failed.",
            "error"
          );
        }
      }
      setDirty(false);
      handleAlert(
        publish === true ? "Published" : "Saved",
        publish === true
          ? "Workshop is now live."
          : "Draft saved successfully.",
        "success"
      );
      // Navigate to edit (with id) so subsequent saves PATCH
      setTimeout(() => navigate(`/workshops`), 600);
    } catch (err) {
      console.error("Save failed", err);
      const detail =
        err?.response?.data?.detail ||
        (err?.response?.data && JSON.stringify(err.response.data)) ||
        "Failed to save workshop.";
      handleAlert("Error", String(detail).slice(0, 300), "error");
    } finally {
      setIsSaving(false);
    }
  };

  // ----- Banner upload handler --------------------------------------------

  const onBannerSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBannerFile(f);
    const reader = new FileReader();
    reader.onload = () => setBannerPreview(reader.result);
    reader.readAsDataURL(f);
    setDirty(true);
  };
  const clearBanner = () => {
    setBannerFile(null);
    setBannerPreview(null);
    setDirty(true);
  };

  // ----- Curriculum mutators ----------------------------------------------

  const addSection = () =>
    setData((d) => ({
      ...d,
      sections: [
        ...d.sections,
        { title: "", description: "", index: d.sections.length, chapters: [] },
      ],
    }));
  const removeSection = (si) =>
    setData((d) => ({
      ...d,
      sections: d.sections.filter((_, i) => i !== si),
    }));
  const updateSection = (si, patch) =>
    setData((d) => ({
      ...d,
      sections: d.sections.map((s, i) => (i === si ? { ...s, ...patch } : s)),
    }));
  const addChapter = (si) =>
    setData((d) => ({
      ...d,
      sections: d.sections.map((s, i) =>
        i === si
          ? {
              ...s,
              chapters: [
                ...(s.chapters || []),
                {
                  title: "",
                  description: "",
                  index: (s.chapters || []).length,
                  videos: [],
                },
              ],
            }
          : s
      ),
    }));
  const removeChapter = (si, ci) =>
    setData((d) => ({
      ...d,
      sections: d.sections.map((s, i) =>
        i === si
          ? { ...s, chapters: s.chapters.filter((_, j) => j !== ci) }
          : s
      ),
    }));
  const updateChapter = (si, ci, patch) =>
    setData((d) => ({
      ...d,
      sections: d.sections.map((s, i) =>
        i === si
          ? {
              ...s,
              chapters: s.chapters.map((c, j) =>
                j === ci ? { ...c, ...patch } : c
              ),
            }
          : s
      ),
    }));
  const addVideo = (si, ci) =>
    setData((d) => ({
      ...d,
      sections: d.sections.map((s, i) =>
        i === si
          ? {
              ...s,
              chapters: s.chapters.map((c, j) =>
                j === ci
                  ? {
                      ...c,
                      videos: [
                        ...(c.videos || []),
                        {
                          title: "",
                          video_url: "",
                          typeof: "2",
                          index: (c.videos || []).length,
                          is_published: true,
                        },
                      ],
                    }
                  : c
              ),
            }
          : s
      ),
    }));
  const removeVideo = (si, ci, vi) =>
    setData((d) => ({
      ...d,
      sections: d.sections.map((s, i) =>
        i === si
          ? {
              ...s,
              chapters: s.chapters.map((c, j) =>
                j === ci
                  ? { ...c, videos: c.videos.filter((_, k) => k !== vi) }
                  : c
              ),
            }
          : s
      ),
    }));
  const updateVideo = (si, ci, vi, patch) =>
    setData((d) => ({
      ...d,
      sections: d.sections.map((s, i) =>
        i === si
          ? {
              ...s,
              chapters: s.chapters.map((c, j) =>
                j === ci
                  ? {
                      ...c,
                      videos: c.videos.map((v, k) =>
                        k === vi ? { ...v, ...patch } : v
                      ),
                    }
                  : c
              ),
            }
          : s
      ),
    }));

  const addFaq = () =>
    setData((d) => ({
      ...d,
      faqs: [
        ...d.faqs,
        { title: "", index: d.faqs.length, answers: [{ answer: "" }] },
      ],
    }));
  const removeFaq = (i) =>
    setData((d) => ({ ...d, faqs: d.faqs.filter((_, j) => j !== i) }));
  const updateFaq = (i, patch) =>
    setData((d) => ({
      ...d,
      faqs: d.faqs.map((f, j) => (j === i ? { ...f, ...patch } : f)),
    }));

  const addMedia = () =>
    setData((d) => ({
      ...d,
      media: [
        ...d.media,
        { title: "", media_url: "", category: 2, index: d.media.length },
      ],
    }));
  const removeMedia = (i) =>
    setData((d) => ({ ...d, media: d.media.filter((_, j) => j !== i) }));
  const updateMedia = (i, patch) =>
    setData((d) => ({
      ...d,
      media: d.media.map((m, j) => (j === i ? { ...m, ...patch } : m)),
    }));

  // ----- Render helpers ----------------------------------------------------

  const renderField = (label, name, child, opts = {}) => (
    <div className="ws-field">
      <span className="ws-field-label">
        {label}
        {opts.required && <span className="required">*</span>}
      </span>
      {child}
      {errors[name] && <span className="ws-field-error">{errors[name]}</span>}
    </div>
  );

  // ----- Step content ------------------------------------------------------

  const StepBasics = (
    <div className="ws-form-section">
      <h3>The basics</h3>
      <p className="help">
        Tell us what this workshop is about. You can save as a draft any time.
      </p>

      <div className="ws-form-row">
        {renderField(
          "Title",
          "title",
          <input
            className="ws-field-input"
            value={data.title}
            onChange={setEv("title")}
            maxLength={255}
            placeholder="e.g. Postpartum recovery essentials"
          />,
          { required: true }
        )}
        {renderField(
          "Workshop category",
          "workshop_type",
          <select
            className="ws-field-select"
            value={data.workshop_type || ""}
            onChange={setEv("workshop_type")}
          >
            <option value="">Select…</option>
            {WORKSHOP_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>,
          { required: true }
        )}
      </div>

      <div className="ws-form-row">
        {renderField(
          "Format (On Demand / Live)",
          "type",
          <MultiPicker
            single
            options={refs.workshopTypes}
            value={data.type}
            onChange={set("type")}
            placeholder="Pick a format…"
          />
        )}
        {renderField(
          "Primary category (taxonomy)",
          "category",
          <MultiPicker
            single
            options={refs.categories}
            value={data.category}
            onChange={set("category")}
            placeholder="Pick a category…"
          />
        )}
      </div>

      <div className="ws-form-row" style={{ gridTemplateColumns: "1fr" }}>
        {renderField(
          "Short description",
          "description",
          <textarea
            className="ws-field-textarea"
            value={data.description}
            onChange={setEv("description")}
            placeholder="One-line summary shown on workshop cards"
          />,
          { required: true }
        )}
      </div>

      <div className="ws-form-row" style={{ gridTemplateColumns: "1fr" }}>
        {renderField(
          "About this workshop",
          "about_workshop",
          <textarea
            className="ws-field-textarea"
            value={data.about_workshop}
            onChange={setEv("about_workshop")}
            placeholder="Detailed description shown on the workshop page"
            style={{ minHeight: 140 }}
          />,
          { required: true }
        )}
      </div>

      <div className="ws-form-row">
        {renderField(
          "Experts / hosts",
          "workshop_experts",
          <MultiPicker
            options={refs.experts}
            value={data.workshop_experts}
            onChange={set("workshop_experts")}
            placeholder="Add experts…"
          />
        )}
        {renderField(
          "Additional categories",
          "categories",
          <MultiPicker
            options={refs.categories}
            value={data.categories}
            onChange={set("categories")}
            placeholder="Add categories…"
          />
        )}
      </div>

      <div className="ws-form-row">
        {renderField(
          "Intended audience",
          "intended_audience",
          <MultiPicker
            single
            options={refs.audiences}
            value={data.intended_audience}
            onChange={set("intended_audience")}
            placeholder="Pick an audience…"
          />
        )}
        {renderField(
          "All audiences",
          "audience",
          <MultiPicker
            options={refs.audiences}
            value={data.audience}
            onChange={set("audience")}
            placeholder="Add audiences…"
          />
        )}
      </div>

      <div className="ws-form-row">
        {renderField(
          "Tags",
          "tags",
          <MultiPicker
            options={refs.tags}
            value={data.tags}
            onChange={set("tags")}
            placeholder="Add tags…"
          />
        )}
        {renderField(
          "Banner image",
          "banner",
          <label className="ws-upload">
            {bannerPreview ? (
              <div className="ws-upload-preview">
                <img src={bannerPreview} alt="Banner preview" />
                <button
                  type="button"
                  className="ws-upload-remove"
                  onClick={(e) => {
                    e.preventDefault();
                    clearBanner();
                  }}
                >
                  ×
                </button>
              </div>
            ) : (
              <span>
                Click to upload a banner image
                <br />
                <small style={{ color: "#888" }}>
                  Recommended 1200×600 px
                </small>
              </span>
            )}
            <input type="file" accept="image/*" onChange={onBannerSelect} />
          </label>
        )}
      </div>

      <div className="ws-form-row">
        {renderField(
          "Start date / time",
          "start_date_time",
          <input
            type="datetime-local"
            className="ws-field-input"
            value={data.start_date_time}
            onChange={setEv("start_date_time")}
          />
        )}
        {renderField(
          "End date / time",
          "end_date_time",
          <input
            type="datetime-local"
            className="ws-field-input"
            value={data.end_date_time}
            onChange={setEv("end_date_time")}
          />
        )}
      </div>
      <div className="ws-form-row">
        <label className="ws-field-checkbox">
          <input
            type="checkbox"
            checked={!!data.is_date_only}
            onChange={setBool("is_date_only")}
          />
          Date only (no specific time)
        </label>
        <label className="ws-field-checkbox">
          <input
            type="checkbox"
            checked={!!data.is_live_recorded_hybrid}
            onChange={setBool("is_live_recorded_hybrid")}
          />
          Hybrid (live + recorded)
        </label>
      </div>

      <div className="ws-form-row">
        {renderField(
          "Live workshop URL",
          "live_workshop_url",
          <input
            className="ws-field-input"
            value={data.live_workshop_url}
            onChange={setEv("live_workshop_url")}
            placeholder="https://zoom.us/…"
          />
        )}
        {renderField(
          "WhatsApp group URL",
          "whatsapp_group_url",
          <input
            className="ws-field-input"
            value={data.whatsapp_group_url}
            onChange={setEv("whatsapp_group_url")}
            placeholder="https://chat.whatsapp.com/…"
          />
        )}
      </div>
    </div>
  );

  const StepPricing = (
    <div className="ws-form-section">
      <h3>Pricing & seats</h3>
      <p className="help">Leave fields empty to skip a currency.</p>

      <div className="ws-form-row">
        {renderField(
          "MRP (₹)",
          "mrp",
          <input
            type="number"
            className="ws-field-input"
            value={data.mrp}
            onChange={setNum("mrp")}
            min="0"
          />
        )}
        {renderField(
          "Selling price (₹)",
          "selling_price",
          <input
            type="number"
            className="ws-field-input"
            value={data.selling_price}
            onChange={setNum("selling_price")}
            min="0"
          />
        )}
        {renderField(
          "Discount (₹)",
          "discount",
          <input
            type="number"
            className="ws-field-input"
            value={data.discount}
            onChange={setNum("discount")}
            min="0"
          />
        )}
      </div>

      <div className="ws-form-row">
        {renderField(
          "MRP ($)",
          "mrp_usd",
          <input
            type="number"
            className="ws-field-input"
            value={data.mrp_usd}
            onChange={setNum("mrp_usd")}
            min="0"
          />
        )}
        {renderField(
          "Selling price ($)",
          "selling_price_usd",
          <input
            type="number"
            className="ws-field-input"
            value={data.selling_price_usd}
            onChange={setNum("selling_price_usd")}
            min="0"
          />
        )}
        {renderField(
          "Discount ($)",
          "discount_for_usd",
          <input
            type="number"
            className="ws-field-input"
            value={data.discount_for_usd}
            onChange={setNum("discount_for_usd")}
            min="0"
          />
        )}
      </div>

      <div className="ws-form-row">
        {renderField(
          "Seats",
          "seats",
          <input
            type="number"
            className="ws-field-input"
            value={data.seats}
            onChange={setNum("seats")}
            min="0"
            disabled={data.is_unlimited_seats}
          />
        )}
        <label className="ws-field-checkbox">
          <input
            type="checkbox"
            checked={!!data.is_unlimited_seats}
            onChange={setBool("is_unlimited_seats")}
          />
          Unlimited seats
        </label>
        <label className="ws-field-checkbox">
          <input
            type="checkbox"
            checked={!!data.free_trial}
            onChange={setBool("free_trial")}
          />
          Offer free trial
        </label>
        <label className="ws-field-checkbox">
          <input
            type="checkbox"
            checked={!!data.is_first_chapter_free}
            onChange={setBool("is_first_chapter_free")}
          />
          First chapter free
        </label>
      </div>
    </div>
  );

  const StepCurriculum = (
    <div className="ws-form-section">
      <h3>Curriculum</h3>
      <p className="help">
        Group your content into sections → chapters → videos. All optional.
      </p>

      {data.sections.length === 0 && (
        <p className="help" style={{ fontStyle: "italic" }}>
          No sections yet. Click below to add one.
        </p>
      )}

      {data.sections.map((section, si) => (
        <div className="ws-section" key={si}>
          <div className="ws-section-header">
            <strong style={{ color: "#888" }}>{si + 1}.</strong>
            <input
              placeholder="Section title (e.g. Introduction)"
              value={section.title}
              onChange={(e) => updateSection(si, { title: e.target.value })}
            />
            <button
              type="button"
              className="ws-icon-btn"
              title="Remove section"
              onClick={() => removeSection(si)}
            >
              🗑
            </button>
          </div>
          <div className="ws-section-body">
            <textarea
              className="ws-field-textarea"
              placeholder="Section description (optional)"
              value={section.description || ""}
              onChange={(e) =>
                updateSection(si, { description: e.target.value })
              }
              style={{ marginBottom: 12 }}
            />

            {(section.chapters || []).map((chapter, ci) => (
              <div className="ws-chapter" key={ci}>
                <div className="ws-chapter-header">
                  <span style={{ color: "#888" }}>
                    {si + 1}.{ci + 1}
                  </span>
                  <input
                    placeholder="Chapter title"
                    value={chapter.title}
                    onChange={(e) =>
                      updateChapter(si, ci, { title: e.target.value })
                    }
                  />
                  <button
                    type="button"
                    className="ws-icon-btn"
                    title="Remove chapter"
                    onClick={() => removeChapter(si, ci)}
                  >
                    🗑
                  </button>
                </div>
                <textarea
                  className="ws-field-textarea"
                  placeholder="Chapter description (optional)"
                  value={chapter.description || ""}
                  onChange={(e) =>
                    updateChapter(si, ci, { description: e.target.value })
                  }
                  style={{ marginBottom: 8, minHeight: 50 }}
                />
                {(chapter.videos || []).map((video, vi) => (
                  <div className="ws-video" key={vi}>
                    <input
                      placeholder="Video title"
                      value={video.title}
                      onChange={(e) =>
                        updateVideo(si, ci, vi, { title: e.target.value })
                      }
                    />
                    <input
                      placeholder="https://youtube.com/…"
                      value={video.video_url || ""}
                      onChange={(e) =>
                        updateVideo(si, ci, vi, { video_url: e.target.value })
                      }
                    />
                    <select
                      value={video.typeof || ""}
                      onChange={(e) =>
                        updateVideo(si, ci, vi, { typeof: e.target.value })
                      }
                    >
                      <option value="">Type…</option>
                      {VIDEO_TYPEOF_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="ws-icon-btn"
                      title="Remove video"
                      onClick={() => removeVideo(si, ci, vi)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="ws-add-row"
                  onClick={() => addVideo(si, ci)}
                >
                  + Add video
                </button>
              </div>
            ))}

            <button
              type="button"
              className="ws-add-row"
              onClick={() => addChapter(si)}
            >
              + Add chapter
            </button>
          </div>
        </div>
      ))}

      <button type="button" className="ws-btn primary" onClick={addSection}>
        + Add section
      </button>
    </div>
  );

  const StepExtras = (
    <div className="ws-form-section">
      <h3>Highlights & resources</h3>
      <p className="help">Surface what makes this workshop valuable.</p>

      <div className="ws-form-row">
        {renderField(
          "Why this works",
          "why_this_works",
          <MultiPicker
            options={refs.whyWorkshop}
            value={data.why_this_works}
            onChange={set("why_this_works")}
            placeholder="Pick benefits to highlight…"
          />
        )}
        {renderField(
          "What you'll learn",
          "what_you_learn",
          <MultiPicker
            options={refs.whatYouLearn}
            value={data.what_you_learn}
            onChange={set("what_you_learn")}
            placeholder="Pick learning outcomes…"
          />
        )}
      </div>

      <h3 style={{ marginTop: 24 }}>FAQs</h3>
      {data.faqs.length === 0 && (
        <p className="help" style={{ fontStyle: "italic" }}>
          No FAQs yet.
        </p>
      )}
      {data.faqs.map((f, i) => (
        <div className="ws-section" key={i}>
          <div className="ws-section-header">
            <input
              placeholder="Question"
              value={f.title}
              onChange={(e) => updateFaq(i, { title: e.target.value })}
            />
            <button
              type="button"
              className="ws-icon-btn"
              onClick={() => removeFaq(i)}
            >
              🗑
            </button>
          </div>
          <div className="ws-section-body">
            <textarea
              className="ws-field-textarea"
              placeholder="Answer"
              value={(f.answers && f.answers[0]?.answer) || ""}
              onChange={(e) => {
                const newAnswers = [...(f.answers || [{}])];
                newAnswers[0] = { ...(newAnswers[0] || {}), answer: e.target.value };
                updateFaq(i, { answers: newAnswers });
              }}
            />
          </div>
        </div>
      ))}
      <button type="button" className="ws-btn" onClick={addFaq}>
        + Add FAQ
      </button>

      <h3 style={{ marginTop: 24 }}>Promotional media</h3>
      <p className="help">External media (URLs only).</p>
      {data.media.map((m, i) => (
        <div className="ws-form-row" key={i}>
          <input
            className="ws-field-input"
            placeholder="Title"
            value={m.title}
            onChange={(e) => updateMedia(i, { title: e.target.value })}
          />
          <input
            className="ws-field-input"
            placeholder="Media URL"
            value={m.media_url || ""}
            onChange={(e) => updateMedia(i, { media_url: e.target.value })}
          />
          <select
            className="ws-field-select"
            value={m.category || 2}
            onChange={(e) =>
              updateMedia(i, { category: Number(e.target.value) })
            }
          >
            {MEDIA_CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="ws-icon-btn"
            onClick={() => removeMedia(i)}
          >
            🗑
          </button>
        </div>
      ))}
      <button type="button" className="ws-btn" onClick={addMedia}>
        + Add media
      </button>
    </div>
  );

  const summary = useMemo(() => {
    const findTitle = (list, id) =>
      list.find((x) => x.id === id)?.title || "—";
    return {
      typeLabel:
        WORKSHOP_TYPE_OPTIONS.find((o) => o.value === data.workshop_type)
          ?.label || "—",
      formatLabel: findTitle(refs.workshopTypes, data.type),
      categoryLabel: findTitle(refs.categories, data.category),
      audienceLabel: findTitle(refs.audiences, data.intended_audience),
      expertCount: (data.workshop_experts || []).length,
      sectionCount: (data.sections || []).length,
      chapterCount: (data.sections || []).reduce(
        (sum, s) => sum + (s.chapters?.length || 0),
        0
      ),
      videoCount: (data.sections || []).reduce(
        (sum, s) =>
          sum +
          (s.chapters || []).reduce(
            (cs, c) => cs + (c.videos?.length || 0),
            0
          ),
        0
      ),
      faqCount: (data.faqs || []).length,
    };
  }, [data, refs]);

  const StepReview = (
    <div className="ws-form-section">
      <h3>Review & publish</h3>
      <p className="help">
        Double-check before publishing. You can save as a draft and edit later.
      </p>
      <div className="ws-review-card">
        <h4>Basics</h4>
        <dl>
          <dt>Title</dt>
          <dd>{data.title || "—"}</dd>
          <dt>Category</dt>
          <dd>{summary.typeLabel}</dd>
          <dt>Format</dt>
          <dd>{summary.formatLabel}</dd>
          <dt>Primary category</dt>
          <dd>{summary.categoryLabel}</dd>
          <dt>Audience</dt>
          <dd>{summary.audienceLabel}</dd>
          <dt>Experts</dt>
          <dd>{summary.expertCount}</dd>
        </dl>
      </div>
      <div className="ws-review-card">
        <h4>Pricing</h4>
        <dl>
          <dt>INR</dt>
          <dd>
            ₹{data.selling_price || data.mrp || "—"}
            {data.mrp && data.selling_price && data.mrp !== data.selling_price
              ? ` (MRP ₹${data.mrp})`
              : ""}
          </dd>
          <dt>USD</dt>
          <dd>
            ${data.selling_price_usd || data.mrp_usd || "—"}
            {data.mrp_usd &&
            data.selling_price_usd &&
            data.mrp_usd !== data.selling_price_usd
              ? ` (MRP $${data.mrp_usd})`
              : ""}
          </dd>
          <dt>Seats</dt>
          <dd>
            {data.is_unlimited_seats ? "Unlimited" : data.seats || "—"}
          </dd>
        </dl>
      </div>
      <div className="ws-review-card">
        <h4>Content</h4>
        <dl>
          <dt>Sections</dt>
          <dd>{summary.sectionCount}</dd>
          <dt>Chapters</dt>
          <dd>{summary.chapterCount}</dd>
          <dt>Videos</dt>
          <dd>{summary.videoCount}</dd>
          <dt>FAQs</dt>
          <dd>{summary.faqCount}</dd>
          <dt>Media items</dt>
          <dd>{(data.media || []).length}</dd>
        </dl>
      </div>
      <p className="help">
        Choose <strong>Save draft</strong> to keep working, or{" "}
        <strong>Publish</strong> to make this visible to users.
      </p>
    </div>
  );

  const stepContent = [
    StepBasics,
    StepPricing,
    StepCurriculum,
    StepExtras,
    StepReview,
  ][step];

  // ----- Render ------------------------------------------------------------

  return (
    <>
      {(isLoading || isSaving) && <LoadingOverlay />}
      <div className="main-container">
        <div className="ws-form-container">
          <div className="ws-form-page-header">
            <div>
              <h2>{isEdit ? "Edit workshop" : "Create workshop"}</h2>
              <div className="subtitle">
                {data.title || (isEdit ? `Workshop #${id}` : "New workshop")}
              </div>
            </div>
            <div>
              <button
                className="ws-btn"
                onClick={() => {
                  if (dirty) setShowLeaveConfirm(true);
                  else navigate("/workshops");
                }}
              >
                Cancel
              </button>
            </div>
          </div>

          <div className="ws-form-shell">
            <div className="ws-form-stepper">
              {STEPS.map((s, i) => (
                <div
                  key={s.key}
                  className={`ws-step ${i === step ? "active" : ""} ${
                    i < step ? "completed" : ""
                  }`}
                  onClick={() => jumpTo(i)}
                >
                  <span className="ws-step-num">{i + 1}</span>
                  <span>
                    <span className="ws-step-label">{s.label}</span>
                    <span className="ws-step-sub">{s.sub}</span>
                  </span>
                </div>
              ))}
            </div>

            <div className="ws-form-main">
              {stepContent}

              <div className="ws-form-actions">
                <div className="ws-form-actions-left">
                  <button
                    className="ws-btn"
                    onClick={goPrev}
                    disabled={step === 0}
                  >
                    ← Back
                  </button>
                </div>
                <div className="ws-form-actions-right">
                  <button
                    className="ws-btn"
                    onClick={() => submit(false)}
                    disabled={isSaving}
                  >
                    Save draft
                  </button>
                  {step < STEPS.length - 1 ? (
                    <button className="ws-btn primary" onClick={goNext}>
                      Next →
                    </button>
                  ) : (
                    <button
                      className="ws-btn primary"
                      onClick={() => submit(true)}
                      disabled={isSaving}
                    >
                      Publish
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {showLeaveConfirm && (
          <Modal
            title="Discard unsaved changes?"
            onClose={() => setShowLeaveConfirm(false)}
          >
            <p>You have unsaved changes. Leave without saving?</p>
            <div className="confirmation-buttons" style={{ marginTop: 16 }}>
              <button
                className="ws-btn"
                onClick={() => setShowLeaveConfirm(false)}
              >
                Keep editing
              </button>
              <button
                className="ws-btn danger"
                onClick={() => navigate("/workshops")}
              >
                Discard
              </button>
            </div>
          </Modal>
        )}

        {alerts.map((alert) => (
          <CustomAlert
            key={alert.id}
            title={alert.title}
            message={alert.message}
            type={alert.type}
            autoClose={alert.autoClose}
            onClose={() => handleRemoveAlert(alert.id)}
          />
        ))}
      </div>
    </>
  );
};

export default WorkshopForm;
